import { useId, useMemo, useState, type KeyboardEvent } from 'react'
import { Plus } from 'lucide-react'

export type Suggestion = {
  id: string
  label: string
  // A second line: a place's city and its profile's @, a profile's kind.
  detail?: string
  // Other names it is found by, beside the label.
  words?: string
}

type Props = {
  label: string
  // What the field shows when not typing: the chosen item's name, or nothing
  // in a field that adds.
  value: string
  placeholder: string
  suggestions: Suggestion[]
  disabled?: boolean
  onPick: (id: string) => void
  // The last line, creating what was typed; off until the cutover.
  create?: { label: (text: string) => string; disabled?: boolean; onCreate: (text: string) => void }
}

const plain = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const MAX = 8

// A field that suggests as it is typed (PRD.md, item 41), as Google Maps and
// Shopify do: every typed word in the name or another name, without accents,
// in any order; the arrows and Enter pick, Esc goes back. The ARIA combobox
// pattern, so it reads as one.
export function SuggestField({ label, value, placeholder, suggestions, disabled, onPick, create }: Props) {
  // What is being typed; null while not typing, showing the value.
  const [text, setText] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const list = useId()
  const query = text?.trim() ?? ''

  const matches = useMemo(() => {
    const words = plain(query).split(/[^a-z0-9@]+/).filter(Boolean)
    if (!words.length) return []
    const starts = (item: Suggestion) => Number(plain(item.label).startsWith(words[0]))
    return suggestions
      .filter((item) => { const found = plain(`${item.label} ${item.words ?? ''}`); return words.every((word) => found.includes(word)) })
      .sort((left, right) => starts(right) - starts(left))
      .slice(0, MAX)
  }, [query, suggestions])

  const open = text !== null && Boolean(query)
  const count = matches.length + (create ? 1 : 0)

  const pick = (index: number) => {
    if (index < matches.length) onPick(matches[index].id)
    else if (create && !create.disabled) create.onCreate(query)
    else return
    setText(null)
  }

  const key = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') { setText(null); return }
    if (!open || !count) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) => (current + (event.key === 'ArrowDown' ? 1 : count - 1)) % count)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      pick(active)
    }
  }

  return (
    <div className="suggest">
      <input
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={list}
        aria-autocomplete="list"
        aria-activedescendant={open && count ? `${list}-${active}` : undefined}
        autoComplete="off"
        disabled={disabled}
        value={text ?? value}
        placeholder={placeholder}
        // Typing replaces the name shown, as a search box does.
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => { setText(event.target.value); setActive(0) }}
        onKeyDown={key}
        onBlur={() => setText(null)}
      />
      {open && (
        <ul className="suggest__list" role="listbox" id={list}>
          {matches.map((item, index) => (
            <li
              key={item.id}
              id={`${list}-${index}`}
              role="option"
              aria-selected={index === active}
              className={index === active ? 'is-active' : ''}
              // Kept from blurring the field, so the pick lands.
              onMouseDown={(event) => { event.preventDefault(); pick(index) }}
              onMouseEnter={() => setActive(index)}
            >
              <strong>{item.label}</strong>
              {item.detail && <small>{item.detail}</small>}
            </li>
          ))}
          {!matches.length && <li className="suggest__empty" role="presentation">Nada com “{query}”.</li>}
          {create && (
            <li
              id={`${list}-${matches.length}`}
              role="option"
              aria-selected={active === matches.length}
              aria-disabled={create.disabled}
              className={`suggest__create ${active === matches.length ? 'is-active' : ''}`}
              onMouseDown={(event) => { event.preventDefault(); pick(matches.length) }}
              onMouseEnter={() => setActive(matches.length)}
            >
              <strong><Plus size={15} /> {create.label(query)}</strong>
              {create.disabled && <small>Liga na virada para o Guve 2.0</small>}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
